import { useState, useContext, useEffect } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { useAuth } from "contexts/AuthContext";
import AccountContext from "contexts/AccountContext";
import { apiRequest } from "common/apiUtils";
import { CloseIcon, CheckCircleIcon, AlertTriangleIcon } from "assets/svgs";
import BasicSelect from "components/common/FormField/BasicSelect";
import { buildCopyPayload, CopyStepError, prepareCopy } from "components/common/copyToHelpers";

/**
 * CopyToModal Component
 *
 * A modal dialog that allows users to copy a sandbox config record (Item, Loot, Vendor, Price Point, XP, ...) to another
 * sandbox. The record keeps its IDs; the destination PUT endpoint upserts it.
 * Displays item details and provides a dropdown to select the destination sandbox.
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Controls modal visibility
 * @param {Function} props.onClose - Callback to close the modal
 * @param {Object} props.selectedItem - The record to be copied (contains its row key, name, description, etc.)
 * @param {string} props.currentSandboxId - ID of the current sandbox
 * @param {Function} props.onSuccess - Callback for successful copy operation
 * @param {Object} props.pageData - RHDynamicPage page data (Name, RowKey, LegacyRowKey, Endpoints, Columns, DoNotSendKeys)
 * @param {Function} props.onError - Callback for error handling
 * @param {string} [props.friendlyName="Item"] - Display name for the item type
 */
export default function CopyToModal({
  isOpen,
  onClose,
  selectedItem,
  currentSandboxId,
  onSuccess,
  pageData,
  onError,
  friendlyName = "Item",
}) {
  const [step, setStep] = useState("select"); // 'select', 'confirm', 'result'
  const [selectedSandbox, setSelectedSandbox] = useState(null);
  const [errors, setErrors] = useState(null);
  // The result of prepareCopy: the fresh source record and what the destination sandbox already holds
  const [copyPlan, setCopyPlan] = useState(null);
  const [isCopying, setIsCopying] = useState(false);
  const { getAccessToken } = useAuth();
  const { currentStateData } = useContext(AccountContext);

  // Update useEffect to ONLY reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setStep("select");
      setSelectedSandbox(null);
      setErrors(null);
      setCopyPlan(null);
      setIsCopying(false);
    }
  }, [isOpen]); // Only depend on isOpen

  // Filter available sandboxes: exclude current sandbox, readonly sandboxes, and archived sandboxes
  const availableSandboxes =
    currentStateData?.product?.sandboxes?.filter(
      (sandbox) =>
        sandbox.sandboxId !== currentSandboxId && // Exclude current sandbox
        !sandbox.readonly && // Exclude readonly sandboxes
        !sandbox.archive, // Exclude archived sandboxes
    ) || [];

  // Format sandboxes for BasicSelect component
  const sandboxOptions = availableSandboxes.map((sandbox) => ({
    id: sandbox.sandboxId,
    title: `${sandbox.sandboxName} (${sandbox.shortName})`,
  }));

  const handleSandboxChange = (e) => {
    const selectedId = e.target.value;
    // Find the complete sandbox object from availableSandboxes
    const sandbox = availableSandboxes.find((s) => s.sandboxId === selectedId);

    // Log for debugging
    console.debug("Sandbox selected:", { selectedId, sandbox });

    // Ensure we always set a complete sandbox object or null
    setSelectedSandbox(selectedId ? sandbox : null);
  };

  // Add a useEffect to validate selectedSandbox state
  useEffect(() => {
    if (selectedSandbox && !selectedSandbox.sandboxId) {
      console.error("Invalid sandbox state detected:", selectedSandbox);
      setSelectedSandbox(null);
    }
  }, [selectedSandbox]);

  const idKey = pageData?.RowKey;
  const legacyIdKey = pageData?.LegacyRowKey;
  const objectId = selectedItem?.[idKey];
  const existingItem = copyPlan?.existing;
  const legacyConflict = copyPlan?.legacyConflict;
  const legacyId = legacyIdKey ? (copyPlan?.sourceRecord ?? selectedItem)?.[legacyIdKey] : null;
  const idLabel = pageData?.Columns?.[idKey]?.title || "ID";
  const legacyIdLabel = pageData?.Columns?.[legacyIdKey]?.title || "Legacy ID";

  const parseApiError = (error) => {
    const apiError = error instanceof CopyStepError ? error.cause : error;
    let errorData = {};
    try {
      errorData = apiError?.errorMsg ? JSON.parse(apiError.errorMsg) : {};
    } catch {
      errorData = { desc: apiError.errorMsg };
    }
    // FastAPI request validation errors (422) carry a "detail" list instead of error_code/desc
    if (!errorData.desc && Array.isArray(errorData.detail)) {
      errorData.desc = errorData.detail.map((d) => `${(d.loc || []).join(".")}: ${d.msg}`).join("; ");
    }
    return {
      title: error instanceof CopyStepError ? error.title : "Copy Operation Failed",
      status: apiError?.response?.status,
      statusText: apiError?.response?.statusText,
      ...errorData,
      desc: errorData.desc || apiError?.message,
    };
  };

  const showFailure = (error) => {
    const errorData = parseApiError(error);
    setErrors([errorData]);
    setStep("result");
    onError(errorData.desc || errorData.title);
  };

  const putCopy = async (plan) => {
    await apiRequest(
      await getAccessToken(),
      plan.putEndpoint,
      { sandbox_id: selectedSandbox.sandboxId, [idKey]: objectId },
      buildCopyPayload(plan.sourceRecord),
    );
    setStep("result");
    onSuccess(
      `${friendlyName} ${plan.existing ? "updated" : "copied"} successfully in ${selectedSandbox.sandboxName}!`,
    );
  };

  /**
   * Reads the source record, checks the destination sandbox, and copies straight away unless the destination already
   * holds the record (confirm the update) or its legacy ID (conflict).
   */
  const handleCopy = async () => {
    if (!selectedSandbox?.sandboxId) return;

    setIsCopying(true);
    try {
      const token = await getAccessToken();
      const plan = await prepareCopy({
        request: (endpoint, replacements, queryParams) =>
          apiRequest(token, endpoint, replacements, null, null, queryParams ? { queryParams } : {}),
        pageData,
        sourceSandboxId: currentSandboxId,
        targetSandboxId: selectedSandbox.sandboxId,
        objectId,
      });
      setCopyPlan(plan);

      if (plan.existing || plan.legacyConflict) {
        setStep("confirm");
        return;
      }
      await putCopy(plan);
    } catch (error) {
      showFailure(error);
    } finally {
      setIsCopying(false);
    }
  };

  const handleConfirmUpdate = async () => {
    setIsCopying(true);
    try {
      await putCopy(copyPlan);
    } catch (error) {
      showFailure(error);
    } finally {
      setIsCopying(false);
    }
  };

  const backToSelect = () => {
    setStep("select");
    setErrors(null);
    setCopyPlan(null);
  };

  const handleClose = () => {
    onClose();
  };

  const renderContent = () => {
    if (step === "select") {
      return (
        <div>
          {/* Current Item Summary */}
          <div className="bg-black/20 rounded-lg border border-white/5 mb-4">
            <div className="px-4 py-2.5 border-b border-white/5">
              <h3 className="text-xs font-medium text-white/50 uppercase tracking-wider">
                Source {friendlyName} Details
              </h3>
            </div>

            {/* Details List */}
            <div className="divide-y divide-white/5">
              {/* IDs Row */}
              <div className={`px-4 py-2.5 grid gap-6 ${legacyIdKey ? "grid-cols-[1fr,auto]" : "grid-cols-1"}`}>
                <div className="min-w-0">
                  <div className="text-xs font-medium text-white/40 mb-1">{idLabel}</div>
                  <code className="text-sm text-white/90 font-mono bg-black/20 px-2.5 py-1.5 rounded block overflow-x-auto">
                    {objectId}
                  </code>
                </div>
                {legacyIdKey && (
                  <div>
                    <div className="text-xs font-medium text-white/40 mb-1">{legacyIdLabel}</div>
                    <code className="text-sm text-white/90 font-mono bg-black/20 px-2.5 py-1.5 rounded inline-block min-w-[80px] text-center">
                      {legacyId || "—"}
                    </code>
                  </div>
                )}
              </div>

              {/* Name and Description */}
              <div className="px-4 py-2.5 space-y-3">
                <div>
                  <div className="text-xs font-medium text-white/40 mb-1">
                    {pageData?.Columns?.name?.title || "Name"}
                  </div>
                  <div className="text-sm text-white/90">{selectedItem?.name || "—"}</div>
                </div>
                <div>
                  <div className="text-xs font-medium text-white/40 mb-1">
                    {pageData?.Columns?.description?.title || "Description"}
                  </div>
                  <div className="text-sm text-white/90 max-h-20 overflow-y-auto">
                    {selectedItem?.description || "—"}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Destination Selection */}
          <div className="bg-black/20 rounded-lg border border-white/5 mb-4">
            <div className="px-4 py-2.5 border-b border-white/5">
              <h3 className="text-xs font-medium text-white/50 uppercase tracking-wider">Select Destination</h3>
            </div>
            <div className="p-4">
              <BasicSelect
                label="Destination Sandbox"
                value={selectedSandbox?.sandboxId || ""}
                onChange={handleSandboxChange}
                options={sandboxOptions}
                placeholder="Choose a sandbox..."
                required={true}
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-3 border-t border-white/5 mt-auto">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-md text-sm font-medium text-white/60 hover:text-white/90 hover:bg-white/5 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleCopy}
              disabled={!selectedSandbox || isCopying}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all
                ${
                  !selectedSandbox || isCopying
                    ? "bg-violet-500/30 text-white/50 cursor-not-allowed"
                    : "bg-violet-500 text-white hover:bg-violet-400 active:bg-violet-600"
                }`}
            >
              {!selectedSandbox
                ? "Select a destination"
                : isCopying
                  ? "Copying..."
                  : `Copy to ${selectedSandbox.sandboxName}`}
            </button>
          </div>
        </div>
      );
    }

    if (step === "confirm") {
      const conflictLabel = legacyConflict?.name
        ? `${legacyConflict.name} (${legacyConflict[idKey]})`
        : legacyConflict?.[idKey];

      return (
        <div>
          <div className="bg-amber-500/10 rounded-lg border border-amber-500/20 p-4 mb-4">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangleIcon className="w-5 h-5 text-amber-500 flex-shrink-0" />
              <span className="font-medium text-amber-500">
                {existingItem ? `${friendlyName} Already Exists` : `${legacyIdLabel} Already In Use`}
              </span>
            </div>
            {existingItem ? (
              <>
                <p className="text-sm text-white/90 mb-4">
                  {friendlyName} {objectId} already exists in target sandbox ({selectedSandbox?.sandboxName}).
                </p>
                <p className="text-sm text-white/70">Would you like to update the existing {friendlyName}?</p>
              </>
            ) : (
              <p className="text-sm text-white/90">
                {legacyIdLabel} {legacyId} is already used by {conflictLabel} in target sandbox (
                {selectedSandbox?.sandboxName}), so this {friendlyName} cannot be copied with its {legacyIdLabel}.
              </p>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-3 border-t border-white/5">
            <button
              onClick={backToSelect}
              className="px-4 py-2 rounded-md text-sm font-medium text-white/60 hover:text-white/90 hover:bg-white/5 transition-colors"
            >
              {existingItem ? "Cancel" : "Back"}
            </button>
            {existingItem && (
              <button
                onClick={handleConfirmUpdate}
                disabled={isCopying}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                  isCopying
                    ? "bg-amber-500/30 text-white/50 cursor-not-allowed"
                    : "bg-amber-500 text-white hover:bg-amber-400"
                }`}
              >
                {isCopying ? "Updating..." : `Yes, Update ${friendlyName}`}
              </button>
            )}
          </div>
        </div>
      );
    }

    return (
      <div>
        <div className="px-4 py-2.5 border-b border-white/5">
          <h3 className="text-xs font-medium text-white/50 uppercase tracking-wider">COPY RESULT</h3>
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className="p-4 space-y-4">
            {errors ? (
              <>
                <div className="flex items-center gap-2">
                  <AlertTriangleIcon className="w-5 h-5 text-amber-500 flex-shrink-0" />
                  <span className="font-medium text-amber-500">
                    {errors[0].title}
                    {errors[0].status ? ` (HTTP ${errors[0].status} ${errors[0].statusText})` : ""}
                  </span>
                </div>

                {(errors[0].error_code || errors[0].desc) && (
                  <div className="bg-black/20 rounded-lg p-4 border border-white/5">
                    <div className="text-sm text-white/50 mb-2">Error Code:</div>
                    <pre className="text-sm text-white/90 font-mono whitespace-pre-wrap break-words bg-black/30 p-3 rounded">
                      {JSON.stringify(
                        {
                          error_code: errors[0].error_code,
                          desc: errors[0].desc,
                        },
                        null,
                        2,
                      )}
                    </pre>
                  </div>
                )}
              </>
            ) : (
              <>
                <div className="flex items-center gap-2">
                  <CheckCircleIcon className="w-5 h-5 text-emerald-500" />
                  <span className="font-medium text-emerald-500">
                    Copy Operation Successful (HTTP {existingItem ? "200 OK" : "201 Created"})
                  </span>
                </div>

                <div className="bg-black/20 rounded-lg p-4 border border-white/5">
                  <div className="text-sm text-white/50 mb-2">Details:</div>
                  <div className="text-sm text-white/90">
                    Successfully {existingItem ? "updated" : "copied"} {friendlyName} record {objectId}
                    {selectedItem?.name ? ` (${selectedItem.name})` : ""} to {selectedSandbox.sandboxName}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-3 p-4 border-t border-white/5">
          <button
            onClick={backToSelect}
            className="px-4 py-2 rounded-md text-sm font-medium text-white/60 hover:text-white/90 hover:bg-white/5 transition-colors"
          >
            Back
          </button>
          <button
            onClick={handleClose}
            className="px-4 py-2 rounded-md text-sm font-medium bg-violet-500 text-white hover:bg-violet-400"
          >
            {errors ? "Close" : "Done"}
          </button>
        </div>
      </div>
    );
  };

  if (!isOpen) return null;

  return createPortal(
    <AnimatePresence mode="wait">
      <motion.div
        className="fixed inset-0 bg-black/80 flex items-center justify-center z-[100]"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={(e) => {
          if (e.target === e.currentTarget) handleClose();
        }}
      >
        <motion.div
          className="p-[1px] rounded-lg bg-gradient-to-r from-pink-500 to-indigo-500 overflow-hidden"
          initial={{ scale: 0.9 }}
          animate={{ scale: 1 }}
          exit={{ scale: 0.9 }}
        >
          <div className="bg-zinc-900 rounded-lg w-[600px] max-h-[80vh] flex flex-col">
            {/* Header */}
            <div className="flex justify-between items-start p-6 pb-0">
              <div>
                <h2 className="text-lg font-medium text-white">Copy {friendlyName}</h2>
                <p className="text-sm text-white/60 mt-1">
                  {step === "select"
                    ? "Review the details and select a destination sandbox"
                    : step === "confirm"
                      ? existingItem
                        ? `${friendlyName} already exists. Confirm the update.`
                        : `${legacyIdLabel} conflict in the destination sandbox.`
                      : "Review the copy operation result"}
                </p>
              </div>
              <button
                onClick={handleClose}
                className="text-white/50 hover:text-white/90 transition-colors p-1 hover:bg-white/10 rounded-full"
              >
                <CloseIcon className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto min-h-0">{renderContent()}</div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body,
  );
}
